import { MdChevronRight } from 'react-icons/md';
import { tr } from '../../i18n/i18n';
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  academicIcon,
  communicationIcon,
  DashBoardIcon,
  financeIcon,
  libraryIcon,
  ManageIcon,
  MasterDataIcon,
  staffUsersIcon,
  changePasswordIcon,
  myInstituteIcon,
  BillingIcon,
  classveewOrgMgtIcon,
  reportIcon,
  newsFeedIcon,
  studentManagementIcon,
  teacherManagementIcon,
  classveewAiIcon,
  etlSetupIcon,
} from '../../assets/images/sidebar-items';
import {
  APP_FEATURES,
  SIDE_BAR_ITEMS,
  SIDE_MENU_BOTTOM_ITEMS,
  AUTHERIZED_USER,
  RESTRICTED_ALLOWED_ROUTES,
} from '../../constants';
import { SESSION_STORAGE_KEY, USER_ROLES } from '../../constant/auth.constant';
import { APP_ROUTES, APP_STATUS } from '../../constants';
import { ISideBarItem } from '../../interface/main.interface';
import { useAppSelector, useAppDispatch } from '../../redux/stateHooks';
import { logOut } from '../../redux/action/auth.action';
import { resetForm } from '../../redux/slices/teacherFormSliceModel';
import { resetSubscriptionState } from '../../redux/slices/subscriptionSliceModel';
import { getSessionItemWithExpiry } from '../../utility/utility';
import { useGracePeriodStatus } from '../../hooks/useGracePeriodStatus';
import { useTrialStatus } from '../../hooks/useTrialStatus';
import { useFeatures } from '../../hooks/useFeature';
import { ConfirmationModal, ModalHandle } from '../Modals';
import GuardianStudentSelector from './GuardianStudentSelector';
import './index.css';

const iconMap = {
  DashBoardIcon,
  ManageIcon,
  MasterDataIcon,
  financeIcon,
  staffUsersIcon,
  communicationIcon,
  academicIcon,
  libraryIcon,
  changePasswordIcon,
  myInstituteIcon,
  BillingIcon,
  classveewOrgMgtIcon,
  reportIcon,
  newsFeedIcon,
  studentManagementIcon,
  teacherManagementIcon,
  classveewAiIcon,
  etlSetupIcon,
};

// Render an icon either from iconMap (image) or a PrimeIcons class (font icon)
// Defaults use a compact size so icons remain concise next to labels
const renderNavIcon = (
  icon?: string,
  imgClass = 'w-5 h-5',
  iClass = 'text-base',
  label?: string,
) => {
  if (!icon) return null;

  const mapped = iconMap[icon as keyof typeof iconMap];

  if (mapped) {
    return <img src={mapped} className={imgClass} alt={label} />;
  }

  // Support PrimeIcons class names like "pi pi-sparkles"
  if (/\bpi\b/.test(icon)) {
    // preserve any extra classes the developer provided
    const className = `${icon} ${iClass}`.trim();
    // Force purple for the Classveew AI sparkles icon (covers both exact label and icon class)
    if (icon.includes('sparkles') || (label && label.includes('Classveew AI'))) {
      return <i className={className} aria-hidden="true" style={{ color: '#6C4E9A' }} />;
    }
    return <i className={className} aria-hidden="true" />;
  }

  return null;
};

const hasPrivilege = (userPrivileges: string[], requiredPrivileges?: APP_FEATURES[]): boolean => {
  if (!requiredPrivileges || requiredPrivileges.length === 0) return true;
  const result = requiredPrivileges.some((p) => userPrivileges.includes(p));

  return result;
};

// Check if user has subscription feature access
const hasSubscriptionFeature = (
  featureCode: string | undefined,
  subscriptionFeatures: Record<string, any>,
): boolean => {
  if (!featureCode) return true; // No feature requirement = allowed
  const value = subscriptionFeatures[featureCode];
  if (value === undefined) return true; // Feature not loaded yet = allow (don't hide prematurely)
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value !== 'false' && value !== '0' && value !== '';
  if (typeof value === 'number') return value > 0;
  return true;
};

const isRouteActive = (route: string | undefined, activePath: string): boolean => {
  if (!route) return false;
  const routePattern = new RegExp(`^${route.replace(/:[^\s/]+/g, '([^/]+)')}$`);
  return routePattern.test(activePath);
};

const isDescendantActive = (item: ISideBarItem, activePath: string): boolean => {
  if (!item.children) return false;
  return item.children.some(
    (child) => isRouteActive(child.route, activePath) || isDescendantActive(child, activePath),
  );
};

const findActiveParentLabel = (items: ISideBarItem[], activePath: string): string | null => {
  for (const item of items) {
    if (
      item.children?.some(
        (child) => isRouteActive(child.route, activePath) || isDescendantActive(child, activePath),
      )
    ) {
      return item.label;
    }
  }
  return null;
};

/**
 * Map a menu label (defined in constants.ts) to its i18n key.
 * e.g. "Organization Management - Create" -> "menu.organization_management_create"
 * Keys live under the `menu` namespace in en.json / ar.json.
 */
const menuKey = (label?: string): string =>
  !label
    ? ''
    : 'menu.' +
      label
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');

const MenuItem: React.FC<{
  item: ISideBarItem;
  userPrivileges: string[];
  subscriptionFeatures: Record<string, any>;
  activeItem: string;
  handleNavigation: (path: string, isAllowed: boolean) => void;
  isOpen: boolean;
  onToggle: () => void;
  isExpanded: boolean;
}> = ({
  item,
  userPrivileges,
  subscriptionFeatures,
  activeItem,
  handleNavigation,
  isOpen,
  onToggle,
  isExpanded,
}) => {
  const { t } = useTranslation();
  // Translated label (falls back to the English label if the key is missing)
  const label = item.label ? t(menuKey(item.label), item.label) : item.label;
  // IMPORTANT: useState must be called unconditionally BEFORE any early returns
  // (Rules of Hooks — hook count must be constant across renders)
  const [isHovering, setIsHovering] = useState(false);

  // Early return if item should not be shown
  if (item.isShown === false) {
    return null;
  }

  // Check subscription feature access
  if (!hasSubscriptionFeature(item.subscriptionFeature, subscriptionFeatures)) {
    return null;
  }

  const isItemAllowed = hasPrivilege(userPrivileges, item.privileges);

  const visibleDropdownChildren = item.children?.filter(
    (child) =>
      child.isShown !== false &&
      hasPrivilege(userPrivileges, child.privileges) &&
      hasSubscriptionFeature(child.subscriptionFeature, subscriptionFeatures),
  );

  const isCollapsible = visibleDropdownChildren && visibleDropdownChildren.length > 0;

  const isLink = item.route && isItemAllowed;

  if (!isLink && !isCollapsible) {
    return null;
  }

  const hasActiveChild = isDescendantActive(item, activeItem);
  const isActive = isRouteActive(item.route, activeItem);

  if (!isExpanded) {
    return (
      <li
        className="relative my-2"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        <div
          className={`flex items-center justify-center cursor-pointer p-3 rounded-md transition-colors duration-200 ${
            hasActiveChild || isActive ? 'bg-purple-100' : 'hover:bg-gray-100'
          }`}
          onClick={() => isLink && handleNavigation(item.route, isItemAllowed)}
        >
          {item.icon && renderNavIcon(item.icon, 'w-5 h-5', 'text-base', item.label)}
        </div>
        {isHovering && isCollapsible && (
          <div className="absolute left-full top-0 ml-2 w-56 bg-white shadow-lg rounded-md p-2 z-50 border border-gray-100">
            <h3 className="font-poppins font-semibold p-2 text-sm text-gray-800">{label}</h3>
            <ul>
              {visibleDropdownChildren.map((child) => (
                <li
                  key={child.route}
                  className={`p-2 text-sm rounded-md cursor-pointer hover:bg-gray-100 ${
                    isRouteActive(child.route, activeItem)
                      ? 'text-[#6C4E9A] font-semibold'
                      : 'text-gray-700'
                  }`}
                  onClick={() => handleNavigation(child.route, true)}
                >
                  {child.label ? t(menuKey(child.label), child.label) : child.label}
                </li>
              ))}
            </ul>
          </div>
        )}
      </li>
    );
  }

  if (isCollapsible) {
    return (
      <li className="my-1">
        <div
          className={`flex items-center gap-2 cursor-pointer p-2 rounded-md hover:bg-gray-100 transition-colors duration-200 ${
            hasActiveChild ? 'text-[#6C4E9A] font-semibold' : ''
          }`}
          onClick={isLink ? () => handleNavigation(item.route, isItemAllowed) : onToggle}
        >
          {item.icon &&
            renderNavIcon(item.icon, 'flex-shrink-0 w-5 h-5', 'text-base', `${item.label} Icon`)}
          <span className="font-poppins font-normal text-sm whitespace-nowrap overflow-hidden text-ellipsis flex-grow">
            {label}
          </span>
          <div
            className={`ml-auto transition-transform duration-300 ${
              isOpen ? 'rotate-90' : 'rotate-0'
            }`}
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
          >
            <MdChevronRight />
          </div>
        </div>
        <div
          className={`transition-all duration-300 ease-in-out overflow-hidden ${
            isOpen ? 'max-h-96' : 'max-h-0'
          }`}
        >
          <ul className="pl-6 mt-1 border-l-2 border-gray-200 ml-4">
            {visibleDropdownChildren.map((child) => (
              <MenuItem
                key={child.route || child.label}
                item={child}
                userPrivileges={userPrivileges}
                subscriptionFeatures={subscriptionFeatures}
                activeItem={activeItem}
                handleNavigation={handleNavigation}
                isOpen={false}
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                onToggle={() => {}}
                isExpanded={isExpanded}
              />
            ))}
          </ul>
        </div>
      </li>
    );
  }

  return (
    <li
      className={`my-1 rounded-md cursor-pointer transition-colors duration-200 p-2 ${
        isItemAllowed ? 'hover:bg-gray-100' : 'text-gray-400 cursor-not-allowed'
      } ${isActive ? 'text-[#6C4E9A] font-semibold bg-purple-50' : ''}`}
      onClick={() => handleNavigation(item.route, isItemAllowed)}
    >
      <div className="flex items-center gap-2">
        {item.icon &&
          renderNavIcon(item.icon, 'flex-shrink-0 w-5 h-5', 'text-base', `${item.label} Icon`)}
        <span className="font-poppins font-normal text-sm whitespace-nowrap overflow-hidden text-ellipsis">
          {label}
        </span>
      </div>
    </li>
  );
};

interface IndexProps {
  isExpanded: boolean;
  isPinned?: boolean;
}

const Index: React.FC<IndexProps> = ({ isExpanded, isPinned = true }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const logoutModalRef = useRef<ModalHandle>(null);
  const { status: authStatus } = useAppSelector((state) => state.auth);
  const isLoggingOut = authStatus === APP_STATUS.LOADING;
  const [privileges, setPrivileges] = useState<string[]>([]);
  const [orgLogo, setOrgLogo] = useState(
    localStorage.getItem('orgLogo') || 'https://placehold.co/176x22/6c2d91/ffffff?text=Classveew',
  );
  const { organization, role } = useAppSelector((state) => state.auth);
  const { setupComplete, loading } = useAppSelector((state) => state.etl);

  const orgInitial = organization ? organization.charAt(0).toUpperCase() : 'C';

  const [openMenu, setOpenMenu] = useState(() => {
    const activeParent = findActiveParentLabel(SIDE_BAR_ITEMS, location.pathname);
    if (activeParent) return activeParent;
    return localStorage.getItem('activeMenuItem') || '';
  });

  // Check if user is restricted (uses JWT-based Redux approach)
  const {
    status: gracePeriodStatus,
    loading: graceLoading,
    error: graceError,
  } = useGracePeriodStatus();

  // Trial users should NEVER be restricted - bypass grace period restriction
  const { status: trialStatus } = useTrialStatus();
  const userIsOnTrial = trialStatus?.isTrial === true;
  const isRestricted = (gracePeriodStatus?.isRestricted || false) && !userIsOnTrial;

  // Get subscription features for filtering
  const { features: subscriptionFeatures } = useFeatures();

  // Keep full navigation visible even when subscription access is restricted.
  const filteredSideBarItems = useMemo(() => {
    return SIDE_BAR_ITEMS;
  }, []);

  const filteredBottomItems = useMemo(() => {
    return SIDE_MENU_BOTTOM_ITEMS;
  }, []);

  // Sidebar items to render (respects restriction status)
  const sidebarItemsToRender = filteredSideBarItems;

  useEffect(() => {
    const activeParent = findActiveParentLabel(sidebarItemsToRender, location.pathname);
    if (activeParent) setOpenMenu(activeParent);
  }, [location.pathname, sidebarItemsToRender]);

  const handleMenuToggle = (label: string) => {
    const newOpenMenu = openMenu === label ? '' : label;
    setOpenMenu(newOpenMenu);
    localStorage.setItem('activeMenuItem', newOpenMenu);
  };

  useEffect(() => {
    const storedPrivileges = getSessionItemWithExpiry(SESSION_STORAGE_KEY.PRIVILEGES);
    if (storedPrivileges) setPrivileges(JSON.parse(storedPrivileges));

    // Listen for privileges update event (triggered after ETL completion or re-authentication)
    const handlePrivilegesUpdate = () => {
      const updatedPrivileges = getSessionItemWithExpiry(SESSION_STORAGE_KEY.PRIVILEGES);
      if (updatedPrivileges) {
        setPrivileges(JSON.parse(updatedPrivileges));
      }
    };
    window.addEventListener('privilegesUpdated', handlePrivilegesUpdate);

    return () => {
      window.removeEventListener('privilegesUpdated', handlePrivilegesUpdate);
    };
  }, []);

  // Listen for logo changes
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'orgLogo') {
        setOrgLogo(event.newValue || 'https://placehold.co/176x22/6c2d91/ffffff?text=Classveew');
      }
    };
    window.addEventListener('storage', handleStorage);

    const handleCustomLogoUpdate = () => {
      setOrgLogo(
        localStorage.getItem('orgLogo') ||
          'https://placehold.co/176x22/6c2d91/ffffff?text=Classveew',
      );
    };
    window.addEventListener('orgLogoUpdated', handleCustomLogoUpdate);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('orgLogoUpdated', handleCustomLogoUpdate);
    };
  }, []);

  // The ETL setup gate only applies to Super Admins (mirrors ETLGuard). For everyone
  // else `setupComplete` is never fetched, so it must NOT block their navigation.
  const isSuperAdmin = privileges.includes(APP_FEATURES.INITIAL_SYSTEM_SETUP);
  const setupGateOk = setupComplete || !isSuperAdmin;

  const handleNavigation = (path: string, isAllowed: boolean) => {
    if (isAllowed && setupGateOk && !loading) navigate(path);
  };

  const openLogoutModal = () => logoutModalRef.current?.openModal();

  const handleConfirmLogout = () => {
    dispatch(logOut())
      .unwrap()
      .then(() => {
        localStorage.clear();
        sessionStorage.clear();
        dispatch(resetForm());
        dispatch(resetSubscriptionState());
        logoutModalRef.current?.closeModal();
        navigate(APP_ROUTES.LOGIN);
      })
      .catch((error: unknown) => {
        console.error('Error during sign-out:', error);
        logoutModalRef.current?.closeModal();
      });
  };

  const handleLogoClick = () => {
    // Use the local `privileges` state already available in this component
    if (hasPrivilege(privileges, [APP_FEATURES.VIEW_DASHBOARD]) && setupGateOk && !loading) {
      navigate('/dashboard');
    }
    // If user lacks privilege, do nothing
  };

  return (
    <div className="bg-white text-dark flex flex-col h-full w-full side-menu-parent flex-shrink-0 overflow-hidden">
      {/* Logo Section - Fixed height matching navbar */}
      <div className="h-16 flex items-center shadow-md flex-shrink-0 relative">
        {isExpanded && isPinned ? (
          <div className="flex items-center justify-center w-full px-4">
            <img
              src={orgLogo}
              alt={tr('fe_sidemenu.organization_logo', "Organization Logo")}
              className="h-8 max-w-full object-contain cursor-pointer"
              onClick={handleLogoClick}
            />
          </div>
        ) : (
          <div className="flex items-center justify-center w-20 absolute left-0 top-0 h-full">
            <div
              className="w-10 h-10 bg-[#6C4E9A] rounded-lg flex items-center justify-center cursor-pointer flex-shrink-0"
              onClick={handleLogoClick}
            >
              <span className="text-white font-bold text-lg">{orgInitial}</span>
            </div>
          </div>
        )}
      </div>

      {/* Menu Items - Scrollable */}
      <div className={`flex-grow overflow-y-auto ${isExpanded ? 'p-4' : 'p-2'}`}>
        <nav>
          <ul className="items-start space-y-1">
            {sidebarItemsToRender.map((item, index) => (
              <MenuItem
                key={index}
                item={item}
                userPrivileges={privileges}
                subscriptionFeatures={subscriptionFeatures}
                activeItem={location.pathname}
                handleNavigation={handleNavigation}
                isOpen={openMenu === item.label}
                onToggle={() => handleMenuToggle(item.label)}
                isExpanded={isExpanded}
              />
            ))}
          </ul>
        </nav>
      </div>
      <div className={`${isExpanded ? 'pt-4 px-4' : 'pt-2 px-2'} pb-4`}>
        {/* Guardian-only: combined organization + student selector, rendered as
            its own separated section above the standard bottom items. Picking a
            student exchanges tokens for the student's organization scope. */}
        {role === USER_ROLES.PARENT && (
          <div className="mb-3 pb-3 border-b border-gray-200">
            {isExpanded && (
              <span className="block px-2 mb-1 font-poppins text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                {t('g.viewing_student', 'Viewing Student')}
              </span>
            )}
            <GuardianStudentSelector isExpanded={isExpanded} />
          </div>
        )}
        <nav>
          <ul className="items-start space-y-1">
            {filteredBottomItems.map((item, index) => (
              <MenuItem
                key={index}
                item={item}
                userPrivileges={privileges}
                subscriptionFeatures={subscriptionFeatures}
                activeItem={location.pathname}
                handleNavigation={handleNavigation}
                isOpen={false}
                onToggle={() => handleMenuToggle(item.label)}
                isExpanded={isExpanded}
              />
            ))}

            {/* Logout — moved here from the header; opens a confirmation modal */}
            {isExpanded ? (
              <li
                className="my-1 rounded-md cursor-pointer transition-colors duration-200 p-2 text-red-600 hover:bg-red-50"
                onClick={openLogoutModal}
                role="button"
                aria-label={t('g.logout', 'Logout')}
              >
                <div className="flex items-center gap-2">
                  <i className="pi pi-sign-out flex-shrink-0 text-base" aria-hidden="true" />
                  <span className="font-poppins font-normal text-sm whitespace-nowrap overflow-hidden text-ellipsis">
                    {t('g.logout', 'Logout')}
                  </span>
                </div>
              </li>
            ) : (
              <li className="relative my-2">
                <div
                  className="flex items-center justify-center cursor-pointer p-3 rounded-md text-red-600 transition-colors duration-200 hover:bg-red-50"
                  onClick={openLogoutModal}
                  role="button"
                  aria-label={t('g.logout', 'Logout')}
                >
                  <i className="pi pi-sign-out text-base" aria-hidden="true" />
                </div>
              </li>
            )}
          </ul>
        </nav>
      </div>

      {/* Logout confirmation — portaled to body so the mask (appendTo:'self')
          escapes the sidebar's stacking context and covers the header too */}
      {createPortal(
        <ConfirmationModal
          ref={logoutModalRef}
          title={t('g.confirm_logout', 'Confirm Logout')}
          content={t('g.logout_confirm_text', 'Are you sure you want to log out of your ClassVeew account?')}
          icon="pi-sign-out"
          severity="warning"
          yesLabel={t('g.yes_logout', 'Yes, Logout')}
          noLabel={t('common.button_cancel', 'Cancel')}
          yesLoading={isLoggingOut}
          onYes={handleConfirmLogout}
          onNo={() => logoutModalRef.current?.closeModal()}
          width="440px"
        />,
        document.body,
      )}
    </div>
  );
};

export default React.memo(Index);
